import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Search, MapPin, Calendar, Users, Plus, Minus, Loader2 } from "lucide-react";
import { format, type Locale } from "date-fns";
import { pt, enUS, fr, zhCN } from "date-fns/locale";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { savePendingSearch } from "@/lib/pendingSearch";

// Storage key for persisting search data
const SEARCH_STORAGE_KEY = "bera_stays_search_data";

interface SearchData {
  searchQuery: string;
  dateRange: { from: string | null; to: string | null };
  guests: { adults: number; children: number };
  timestamp: number;
}

const Hero = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  
  // Date-fns locale mapping
  const dateLocales: Record<string, Locale> = {
    pt: pt,
    en: enUS,
    fr: fr,
    zh: zhCN,
  };
  const currentDateLocale = dateLocales[i18n.language] || pt;

  // Load persisted data from localStorage on mount
  const loadPersistedData = () => {
    try {
      const stored = localStorage.getItem(SEARCH_STORAGE_KEY);
      if (stored) {
        const data: SearchData = JSON.parse(stored);
        // Check if data is less than 7 days old
        const isValid = Date.now() - data.timestamp < 7 * 24 * 60 * 60 * 1000;
        if (isValid) {
          return {
            searchQuery: data.searchQuery || "",
            dateRange: {
              from: data.dateRange?.from ? new Date(data.dateRange.from) : undefined,
              to: data.dateRange?.to ? new Date(data.dateRange.to) : undefined,
            },
            guests: data.guests || { adults: 2, children: 0 },
          };
        }
      }
    } catch {
      // Ignore parse errors
    }
    return null;
  };

  const persistedData = loadPersistedData();

  const [searchQuery, setSearchQuery] = useState(persistedData?.searchQuery || "");
  const [dateRange, setDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>(
    persistedData?.dateRange || { from: undefined, to: undefined }
  );
  const [guests, setGuests] = useState(persistedData?.guests || { adults: 2, children: 0 });
  const [isDateOpen, setIsDateOpen] = useState(false);
  const [isGuestsOpen, setIsGuestsOpen] = useState(false);
  const [isAISearching, setIsAISearching] = useState(false);
  const [aiMessage, setAiMessage] = useState<string | null>(null);

  // Persist data to localStorage whenever it changes
  useEffect(() => {
    const dataToStore: SearchData = {
      searchQuery,
      dateRange: {
        from: dateRange.from?.toISOString() || null,
        to: dateRange.to?.toISOString() || null,
      },
      guests,
      timestamp: Date.now(),
    };
    localStorage.setItem(SEARCH_STORAGE_KEY, JSON.stringify(dataToStore));
  }, [searchQuery, dateRange, guests]);

  const buildSearchQuery = (extra?: Record<string, string | undefined>) => {
    const params = new URLSearchParams();
    if (searchQuery) {
      params.set("q", searchQuery);
      params.set("search", searchQuery);
    }
    if (dateRange.from) params.set("checkIn", dateRange.from.toISOString());
    if (dateRange.to) params.set("checkOut", dateRange.to.toISOString());
    if (guests.adults + guests.children > 0) params.set("guests", String(guests.adults + guests.children));
    if (extra) {
      for (const [k, v] of Object.entries(extra)) {
        if (v) params.set(k, v);
      }
    }
    return params.toString();
  };

  const goToExploreOrAuth = (queryString: string) => {
    const target = `/explorar?${queryString}`;
    savePendingSearch("/explorar", `?${queryString}`);
    if (!user) {
      navigate(`/auth?redirect=${encodeURIComponent(target)}`);
      return;
    }
    navigate(target);
  };

  const handleSearch = () => {
    goToExploreOrAuth(buildSearchQuery());
  };

  const handleAISearch = async () => {
    if (!searchQuery.trim()) {
      toast.error(t('hero.searchEmptyError') || "Por favor, digite o que procura");
      return;
    }
    setIsAISearching(true);
    setAiMessage(null);
    try {
      const { data, error } = await supabase.functions.invoke('ai-search-stays', {
        body: {
          query: searchQuery,
          checkIn: dateRange.from?.toISOString(),
          checkOut: dateRange.to?.toISOString(),
          guests: guests.adults + guests.children
        }
      });
      if (error) throw error;
      if (data?.success) {
        setAiMessage(data.message);
        const qs = buildSearchQuery({
          location: data.filters?.location,
          recommended: data.recommended_ids?.length ? data.recommended_ids.join(",") : undefined,
        });
        setTimeout(() => { goToExploreOrAuth(qs); }, 1000);
      } else {
        handleSearch();
      }
    } catch (error: any) {
      console.error("AI Search error:", error);
      handleSearch();
    } finally {
      setIsAISearching(false);
    }
  };

  const totalGuests = guests.adults + guests.children;
  const guestsLabel = totalGuests === 1 ? t('hero.oneGuest') || "1 hóspede" : t('hero.multipleGuests', { count: totalGuests, defaultValue: `${totalGuests} hóspedes` });

  const dateLabel = dateRange.from 
    ? dateRange.to 
      ? `${format(dateRange.from, "dd MMM", { locale: currentDateLocale })} - ${format(dateRange.to, "dd MMM", { locale: currentDateLocale })}`
      : format(dateRange.from, "dd MMM yyyy", { locale: currentDateLocale })
    : t('hero.addDates') || "Adicionar datas";

  // Fetch real counts from DB
  const { data: propertiesCount } = useQuery({
    queryKey: ["landing-properties-count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("properties")
        .select("*", { count: "exact", head: true })
        .eq("status", "active");
      if (error) return 100;
      return Math.max(count || 0, 100);
    },
    staleTime: 60000,
  });

  const { data: guestsCount } = useQuery({
    queryKey: ["landing-guests-count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("profiles")
        .select("*", { count: "exact", head: true });
      if (error) return 200;
      return Math.max(count || 0, 200);
    },
    staleTime: 60000,
  });

  // Fetch occupancy rate: admin override OR calculated from bookings (real-time)
  const { data: occupancyRate, refetch: refetchOccupancy } = useQuery({
    queryKey: ["landing-occupancy-rate"],
    queryFn: async () => {
      // Check admin override first
      const { data: setting } = await supabase
        .from("platform_settings")
        .select("value")
        .eq("key", "occupancy_rate_override")
        .maybeSingle();

      if (setting?.value && setting.value !== "null" && setting.value !== null) {
        const parsed = typeof setting.value === 'string' ? parseFloat(setting.value) : Number(setting.value);
        if (!isNaN(parsed) && parsed > 0) return parsed;
      }

      // Calculate from confirmed bookings in last 90 days
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
      
      const { data: bookings } = await supabase
        .from("bookings")
        .select("nights")
        .in("payment_status", ["paid"])
        .gte("created_at", ninetyDaysAgo.toISOString());
      
      const { count: totalProps } = await supabase
        .from("properties")
        .select("*", { count: "exact", head: true })
        .eq("status", "active");

      if (!totalProps || totalProps === 0) return 45;

      const totalBookedNights = (bookings || []).reduce((sum, b) => sum + (b.nights || 0), 0);
      const totalAvailableNights = totalProps * 90;
      const calculated = Math.round((totalBookedNights / totalAvailableNights) * 100);
      
      return Math.max(calculated, 45); // minimum 45%
    },
    staleTime: 30000,
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
  });

  // Real-time subscription: refetch occupancy when bookings or override changes
  useEffect(() => {
    const channel = supabase
      .channel("landing-occupancy-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "bookings" }, () => {
        refetchOccupancy();
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "platform_settings", filter: "key=eq.occupancy_rate_override" }, () => {
        refetchOccupancy();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [refetchOccupancy]);

  const formatCount = (n: number) => {
    if (n >= 1000) return `+${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1).replace(".", ".")}k`;
    return `+${n}`;
  };

  const stats = [
    { value: formatCount(propertiesCount ?? 100), label: t('hero.statsProperties') || "Propriedades" },
    { value: formatCount(guestsCount ?? 200), label: t('hero.statsGuests') || "Hóspedes", icon: Users },
    { value: `${occupancyRate ?? 45}%`, label: t('hero.statsOccupancy') || "Taxa de Ocupação" },
    { value: "21", label: t('hero.statsProvinces') || "Províncias", icon: MapPin },
  ];

  return (
    <section className="relative flex items-center pt-24 pb-10 md:pt-28 md:pb-12 overflow-hidden min-h-[72vh]">
      {/* Background - Interior bedroom image with coral overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1631049307264-da0ec9d70304?q=80&w=2070')",
        }}
      />
      {/* Coral gradient overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#E8513D]/85 via-[#D94F30]/75 to-[#B83A28]/80" />
      <div className="absolute inset-0 bg-black/20" />

      {/* Ambient orbs - more subtle */}
      <motion.div
        className="absolute top-1/4 right-20 w-80 h-80 bg-primary/10 rounded-full blur-[100px]"
        animate={{ scale: [1, 1.15, 1], opacity: [0.2, 0.35, 0.2] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-1/4 left-20 w-[28rem] h-[28rem] bg-bera-gold/10 rounded-full blur-[120px]"
        animate={{ scale: [1.05, 0.95, 1.05], opacity: [0.15, 0.3, 0.15] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Heading - refined typography with multi-color styling */}
          <motion.h1
            className="font-display text-[2rem] md:text-4xl lg:text-[3.25rem] font-bold mb-2.5 tracking-[-0.02em] leading-[1.3]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <span className="text-white block mb-3 drop-shadow-lg">{t('hero.titleFind') || "Encontre em Angola"}</span>
            <span className="text-white drop-shadow-lg">{t('hero.titleApts') || "Apartamentos"}</span>
            <span className="text-white drop-shadow-lg">, </span>
            <span className="bg-gradient-to-r from-white via-[#FFE4D1] to-[#FFD4A3] bg-clip-text text-transparent drop-shadow-lg">
              {t('hero.titleHotels') || "Hotéis e Hospedarias"}
            </span>
          </motion.h1>

          <motion.p
            className="text-[15px] md:text-base text-white/90 mb-7 max-w-lg mx-auto leading-[1.6] drop-shadow-md"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            {t('hero.subtitle')}
          </motion.p>

          {/* Search Box - Elegant Design with glassmorphism */}
          <motion.div
            className="bg-white/95 dark:bg-card/95 backdrop-blur-md rounded-3xl p-3 shadow-[0_8px_40px_-12px_rgba(0,0,0,0.25)] border border-white/30 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr] gap-1">
              {/* Location */}
              <div className="relative px-5 py-4 md:border-r border-border/20 hover:bg-muted/30 rounded-2xl transition-colors duration-200">
                <label className="text-[11px] font-semibold text-muted-foreground mb-2 block text-left uppercase tracking-wide">
                  {t('hero.whereLabel') || "Onde?"}
                </label>
                <div className="relative group">
                  <MapPin className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-primary/60 group-focus-within:text-primary transition-colors" strokeWidth={1.5} />
                  <Input
                    type="text"
                    placeholder={t('hero.wherePlaceholder') || "Luanda, Benguela, Huíla..."}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAISearch()}
                    className="pl-7 h-10 bg-transparent border-0 shadow-none text-[15px] placeholder:text-muted-foreground/50 focus-visible:ring-0 font-medium"
                  />
                </div>
              </div>

              {/* Dates */}
              <div className="relative px-5 py-4 md:border-r border-border/20 hover:bg-muted/30 rounded-2xl transition-colors duration-200">
                <label className="text-[11px] font-semibold text-muted-foreground mb-2 block text-left uppercase tracking-wide">
                  {t('hero.whenLabel') || "Quando?"}
                </label>
                <Popover open={isDateOpen} onOpenChange={setIsDateOpen}>
                  <PopoverTrigger asChild>
                    <button className="relative w-full h-10 pl-7 text-left text-[15px] transition-colors group flex items-center rounded-lg">
                      <Calendar className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-primary/60 group-hover:text-primary transition-colors" strokeWidth={1.5} />
                      <span className={dateRange.from ? "text-foreground font-medium" : "text-muted-foreground/50 font-medium"}>
                        {dateLabel}
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 border-border/20 shadow-2xl rounded-2xl" align="start">
                    <CalendarComponent
                      mode="range"
                      selected={dateRange}
                      onSelect={(range) => {
                        setDateRange({ from: range?.from, to: range?.to });
                        if (range?.to) setIsDateOpen(false);
                      }}
                      numberOfMonths={2}
                      disabled={(date) => date < new Date()}
                      locale={currentDateLocale}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Guests */}
              <div className="relative px-5 py-4 hover:bg-muted/30 rounded-2xl transition-colors duration-200">
                <label className="text-[11px] font-semibold text-muted-foreground mb-2 block text-left uppercase tracking-wide">
                  {t('hero.guestsLabel') || "Hóspedes"}
                </label>
                <Popover open={isGuestsOpen} onOpenChange={setIsGuestsOpen}>
                  <PopoverTrigger asChild>
                    <button className="relative w-full h-10 pl-7 text-left text-[15px] transition-colors group flex items-center rounded-lg">
                      <Users className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-primary/60 group-hover:text-primary transition-colors" strokeWidth={1.5} />
                      <span className="text-foreground font-medium">{guestsLabel}</span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 border-border/20 shadow-2xl rounded-2xl mt-2" align="end">
                    <div className="space-y-4 p-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-foreground text-sm">{t('hero.adults') || "Adultos"}</p>
                          <p className="text-xs text-muted-foreground">{t('hero.adultsDesc') || "13 anos ou mais"}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Button variant="outline" size="icon" className="h-8 w-8 rounded-full border-border/50" onClick={() => setGuests(g => ({ ...g, adults: Math.max(1, g.adults - 1) }))} disabled={guests.adults <= 1}>
                            <Minus className="h-3.5 w-3.5" strokeWidth={1.5} />
                          </Button>
                          <span className="w-5 text-center font-semibold text-sm">{guests.adults}</span>
                          <Button variant="outline" size="icon" className="h-8 w-8 rounded-full border-border/50" onClick={() => setGuests(g => ({ ...g, adults: Math.min(16, g.adults + 1) }))} disabled={guests.adults >= 16}>
                            <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-foreground text-sm">{t('hero.children') || "Crianças"}</p>
                          <p className="text-xs text-muted-foreground">{t('hero.childrenDesc') || "0-12 anos"}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Button variant="outline" size="icon" className="h-8 w-8 rounded-full border-border/50" onClick={() => setGuests(g => ({ ...g, children: Math.max(0, g.children - 1) }))} disabled={guests.children <= 0}>
                            <Minus className="h-3.5 w-3.5" strokeWidth={1.5} />
                          </Button>
                          <span className="w-5 text-center font-semibold text-sm">{guests.children}</span>
                          <Button variant="outline" size="icon" className="h-8 w-8 rounded-full border-border/50" onClick={() => setGuests(g => ({ ...g, children: Math.min(10, g.children + 1) }))} disabled={guests.children >= 10}>
                            <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Search Button - Elegant */}
            <div className="mt-3 flex justify-center px-2 pb-2">
              <motion.button
                onClick={handleAISearch}
                disabled={isAISearching}
                className="relative w-full md:w-auto min-w-[200px] h-14 px-10 rounded-full bg-gradient-to-r from-primary to-bera-terracotta text-white font-semibold text-base shadow-[0_8px_32px_-8px_rgba(180,90,50,0.5)] disabled:opacity-70 overflow-hidden group hover:shadow-[0_12px_40px_-8px_rgba(180,90,50,0.6)] transition-shadow duration-300"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="relative z-10 flex items-center justify-center gap-2.5">
                  {isAISearching ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>{t('hero.searching') || "A procurar..."}</span>
                    </>
                  ) : (
                    <>
                      <Search className="w-5 h-5" strokeWidth={2} />
                      <span>{t('hero.searchButton') || "Pesquisar"}</span>
                    </>
                  )}
                </span>
              </motion.button>
            </div>

            {/* AI Message */}
            <AnimatePresence>
              {aiMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="mt-2 px-4 pb-2"
                >
                  <div className="flex items-center gap-2 text-xs text-primary/80 bg-primary/5 rounded-lg px-3 py-2">
                    <span>{aiMessage}</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Stats - glass cards for visibility on image background */}
          <motion.div
            className="flex flex-wrap justify-center gap-4 md:gap-6 mt-10"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            {stats.map((stat) => (
              <div key={stat.label} className="text-center group cursor-default bg-white/20 backdrop-blur-sm rounded-2xl px-5 py-3 border border-white/20">
                <div className="flex items-center justify-center gap-2 mb-1">
                  {stat.icon && <stat.icon className="w-[18px] h-[18px] text-white/80" strokeWidth={1.5} />}
                  <p className="font-display text-[1.375rem] md:text-[1.625rem] font-bold text-white drop-shadow-md tracking-tight">
                    {stat.value}
                  </p>
                </div>
                <p className="text-white/80 text-[13px] font-medium tracking-wide drop-shadow-sm">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Bottom animated wave - BeraStays coral identity */}
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden">
        <svg viewBox="0 0 1200 150" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full h-20 md:h-28">
          <defs>
            <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#E8513D" stopOpacity="0.4" />
              <stop offset="50%" stopColor="#D94F30" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#E8513D" stopOpacity="0.4" />
            </linearGradient>
          </defs>
          {/* Main wave - BeraStays coral gradient */}
          <path d="M0 75C200 120 400 30 600 75C800 120 1000 30 1200 75V150H0V75Z" fill="url(#waveGradient)">
            <animate attributeName="d" dur="6s" repeatCount="indefinite" values="
              M0 75C200 120 400 30 600 75C800 120 1000 30 1200 75V150H0V75Z;
              M0 75C200 30 400 120 600 75C800 30 1000 120 1200 75V150H0V75Z;
              M0 75C200 120 400 30 600 75C800 120 1000 30 1200 75V150H0V75Z
            " />
          </path>
          {/* Secondary wave - coral accent */}
          <path d="M0 90C200 135 400 45 600 90C800 135 1000 45 1200 90V150H0V90Z" fill="hsl(var(--primary)/0.25)">
            <animate attributeName="d" dur="8s" repeatCount="indefinite" values="
              M0 90C200 135 400 45 600 90C800 135 1000 45 1200 90V150H0V90Z;
              M0 90C200 45 400 135 600 90C800 45 1000 135 1200 90V150H0V90Z;
              M0 90C200 135 400 45 600 90C800 135 1000 45 1200 90V150H0V90Z
            " />
          </path>
        </svg>
      </div>
    </section>
  );
};

export default Hero;
